import { Component } from "react";
import "./MonthlyEntry.css";
import moment from "moment";
import { Table, Tabs, Tab } from "react-bootstrap";
import { CSVLink } from "react-csv";
import axios from "axios";
import IconBuilder from "../../../../utilities/iconBuilder/IconBuilder";
import AxiosConfig from "src/utilities/AxiosConfig";
import Loader from "src/components/common/Loader";
import { DataTable } from "src/components/common/DataTable/components/DataTable";
import type { Column } from "src/components/common/DataTable/types";

type MonthlyEntryProps = {
	fullCalculations: any;
	stateCalculations: any;
	newLeasesByState: any;
	termLeasesByState: any;
};

type MonthlyEntryState = {
	selectedMonth: number;
	selectedYear: number;
	showVehicleBalances: boolean;
	vehicleBalances: any[] | null;
	vehicleBalancesLoading: boolean;
	vehicleBalancesError: string | null;
	vehicleBalanceTab: string;
};

const vehicleBalanceColumns: Column<any>[] = [
	{ accessor: "UnitNumber", label: "Unit #" },
	{
		accessor: "NetLeaseLiability",
		label: "Lease Liability",
		render: (r: any) =>
			"$" +
			r.NetLeaseLiability?.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}),
	},
	{
		accessor: "NetROUAsset",
		label: "ROU Asset",
		render: (r: any) =>
			"$" +
			r.NetROUAsset?.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}),
	},
	{
		accessor: "Payment",
		label: "Payment",
		render: (r: any) =>
			"$" +
			r.Payment?.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}),
	}
];

class MonthlyEntry extends Component<MonthlyEntryProps, MonthlyEntryState> {
	constructor(props: MonthlyEntryProps) {
		super(props);
		this.state = {
			selectedMonth: moment().month(),
			selectedYear: moment().year(),
			showVehicleBalances: false,
			vehicleBalances: null,
			vehicleBalancesLoading: false,
			vehicleBalancesError: null,
			vehicleBalanceTab: "All",
		};
	}

	componentDidUpdate(
		_prevProps: MonthlyEntryProps,
		prevState: MonthlyEntryState,
	) {
		const monthChanged =
			prevState.selectedMonth !== this.state.selectedMonth ||
			prevState.selectedYear !== this.state.selectedYear;

		if (monthChanged) {
			if (this.state.showVehicleBalances) {
				// Expanded: balances are month-scoped, so refresh for the new month.
				this.fetchVehicleBalances();
			} else if (this.state.vehicleBalances !== null) {
				// Collapsed: invalidate the cache so the next expand refetches.
				this.setState({ vehicleBalances: null });
			}
		}
	}

	fetchVehicleBalances = () => {
		this.setState({ vehicleBalancesLoading: true, vehicleBalancesError: null });
		const apiMonth = this.state.selectedMonth + 1; // moment month is 0-indexed; API expects 1-12
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/schedules/by-vehicle?month=${apiMonth}&year=${this.state.selectedYear}`,
				AxiosConfig.configValue("min"),
			)
			.then((res) =>
				this.setState({
					vehicleBalances: res.data?.Vehicles ?? [],
					vehicleBalancesLoading: false,
				}),
			)
			.catch(() =>
				this.setState({
					vehicleBalances: [],
					vehicleBalancesError:
						"An error occurred while loading balances. Please try refreshing the page.",
					vehicleBalancesLoading: false,
				}),
			);
	};

	toggleVehicleBalances = () => {
		const next = !this.state.showVehicleBalances;
		this.setState({ showVehicleBalances: next });
		if (next && !this.state.vehicleBalances) {
			this.fetchVehicleBalances();
		}
	};

	getTabVehicles = (): any[] => {
		const all = this.state.vehicleBalances ?? [];
		return this.state.vehicleBalanceTab === "All"
			? all
			: all.filter((v: any) => v.State === this.state.vehicleBalanceTab);
	};

	getTabTotals = () => {
		const tabVehicles = this.getTabVehicles();
		return {
			totalLiability: tabVehicles.reduce(
				(sum: number, v: any) => sum + (v.NetLeaseLiability ?? 0),
				0,
			),
			totalROU: tabVehicles.reduce(
				(sum: number, v: any) => sum + (v.NetROUAsset ?? 0),
				0,
			),
			totalPayments: tabVehicles.reduce(
				(sum: number, v: any) => sum + (v.Payment ?? 0),
				0,
			),
		};
	};

	getVehicleBalancesCSV = () => {
		const rows = this.getTabVehicles().map((v: any) => ({
			"Unit #": v.UnitNumber,
			State: v.State,
			"Lease Liability": v.NetLeaseLiability,
			"ROU Asset": v.NetROUAsset,
			"Payment": v.Payment
		}));

		if (rows.length === 0) return rows;

		const { totalLiability, totalROU, totalPayments } = this.getTabTotals();
		rows.push({
			"Unit #": "Total",
			State: "",
			"Lease Liability": totalLiability,
			"ROU Asset": totalROU,
			"Payment": totalPayments
		});
		return rows;
	};

	renderVehicleBalanceTotals = () => {
		const tabVehicles = this.getTabVehicles();

		if (tabVehicles.length === 0) return null;

		// Sum the full filtered set (every page), not just the rows on screen.
		const { totalLiability, totalROU, totalPayments } = this.getTabTotals();

		const fmt = (n: number) =>
			"$" +
			n.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});

		return (
			<div className="vehicle-balances-totals">
				<span className="vbt-label">
					Total ({this.state.vehicleBalanceTab})
				</span>
				<span className="vbt-figure">Lease Liability: {fmt(totalLiability)}</span>
				<span className="vbt-figure">ROU Asset: {fmt(totalROU)}</span>
				<span className="vbt-figure">Payment: {fmt(totalPayments)}</span>
			</div>
		);
	};

	getCurrentPeriodForState = (state: string) => {
		return this.props.stateCalculations[state].find(
			(p: any) =>
				moment(p.Date).month() === this.state.selectedMonth &&
				moment(p.Date).year() === this.state.selectedYear,
		);
	};

	getCurrentPeriodTotal = () => {
		return this.props.fullCalculations?.find(
			(p: any) =>
				moment(p.Date).month() === this.state.selectedMonth &&
				moment(p.Date).year() === this.state.selectedYear,
		);
	};

	getNewLeasesForMonth = (month: number, year: number) => {
		if (!this.props.newLeasesByState) return null;
		const result: any = {};
		Object.keys(this.props.newLeasesByState).forEach((state) => {
			const match = this.props.newLeasesByState[state].find(
				(p: any) =>
					moment(p.Date).month() === month && moment(p.Date).year() === year,
			);
			if (match) {
				result[state] = match;
			}
		});
		if (Object.keys(result).length === 0) return null;
		return result;
	};

	getTerminatedLeasesForMonth = (month: number, year: number) => {
		if (!this.props.termLeasesByState) return null;
		const result: any = {};
		Object.keys(this.props.termLeasesByState).forEach((state) => {
			const match = this.props.termLeasesByState[state].find(
				(p: any) =>
					moment(p.Date).month() === month && moment(p.Date).year() === year,
			);
			if (match) {
				result[state] = match;
			}
		});
		if (Object.keys(result).length === 0) return null;
		return result;
	};

	getMonthlyEntryCSV = () => {
		const rows: any[] = [];
		const month = this.state.selectedMonth;
		const year = this.state.selectedYear;
		const monthLabel = moment({ year, month }).format("MMMM YYYY");

		// Monthly Totals
		rows.push({ Section: `Monthly Totals - ${monthLabel}` });
		if (this.props.stateCalculations) {
			Object.keys(this.props.stateCalculations).forEach((state) => {
				const period = this.getCurrentPeriodForState(state);
				if (period) {
					rows.push({
						State: state,
						"ROU Asset Amort Exp": period.ROUAssetAmortization,
						"Right of Use Asset": period.ROUAssetAmortization,
						"Lease Liability": period.LeaseLiabilityAmortization,
						"Interest Exp": period.ImputedInterest,
						Cash: period.Payment,
					});
				}
			});
			const totalPeriod = this.getCurrentPeriodTotal();
			if (totalPeriod) {
				rows.push({
					State: "Total",
					"ROU Asset Amort Exp": totalPeriod.ROUAssetAmortization,
					"Right of Use Asset": totalPeriod.ROUAssetAmortization,
					"Lease Liability": totalPeriod.LeaseLiabilityAmortization,
					"Interest Exp": totalPeriod.ImputedInterest,
					Cash: totalPeriod.Payment,
				});
			}
		}

		// New Leases
		const newLeases = this.getNewLeasesForMonth(month, year);
		rows.push({}, {}, {}, {}, {});
		rows.push({
			Section: `New Lease Entries - ${monthLabel}`,
			State: "State",
			"ROU Asset Amort Exp": "(dr) Right of Use Asset",
			"Right of Use Asset": "(cr) Lease Liability",
		});
		if (newLeases) {
			Object.keys(newLeases).forEach((state) => {
				rows.push({
					State: state,
					"ROU Asset Amort Exp": newLeases[state].NetROUAsset,
					"Right of Use Asset": newLeases[state].NetLeaseLiability,
				});
			});
		}

		// Terminated Leases
		const termLeases = this.getTerminatedLeasesForMonth(month, year);
		rows.push({}, {}, {}, {}, {});
		rows.push({
			Section: `Terminated Leases - ${monthLabel}`,
			State: "State",
			"ROU Asset Amort Exp": "(dr) Lease Liability",
			"Right of Use Asset": "(cr) Right of Use Asset",
		});
		if (termLeases) {
			Object.keys(termLeases).forEach((state) => {
				rows.push({
					State: state,
					"ROU Asset Amort Exp": termLeases[state].LeaseLiabilityAmortization,
					"Right of Use Asset": termLeases[state].ROUAssetAmortization,
				});
			});
		}

		return rows;
	};

	renderVehicleBalances = () => {
		if (this.state.vehicleBalancesLoading) {
			return <Loader type="infinite" />;
		}
		if (this.state.vehicleBalancesError) {
			return (
				<div className="vehicle-balances-error">
					{this.state.vehicleBalancesError}
				</div>
			);
		}
		return (
			<>
				<div className="vehicle-balances-export">
					<CSVLink
						data={this.getVehicleBalancesCSV()}
						filename={`VehicleBalances_${
							this.state.vehicleBalanceTab
						}_${moment.months()[this.state.selectedMonth]}_${
							this.state.selectedYear
						}.csv`}
						title="Download CSV"
					>
						<IconBuilder extension="csv" size={32} />
					</CSVLink>
				</div>
				<Tabs
					activeKey={this.state.vehicleBalanceTab}
					onSelect={(k) =>
						this.setState({ vehicleBalanceTab: k || "All" })
					}
				>
					<Tab eventKey="All" title="All" />
					{Array.from(
						new Set(
							(this.state.vehicleBalances ?? []).map(
								(v: any) => v.State,
							),
						),
					)
						.sort()
						.map((state) => (
							<Tab eventKey={state} title={state} key={state} />
						))}
				</Tabs>
				<DataTable
					data={this.state.vehicleBalances ?? []}
					columns={vehicleBalanceColumns}
					filterQuery={
						this.state.vehicleBalanceTab === "All"
							? undefined
							: { State: this.state.vehicleBalanceTab }
					}
					emptyMessage="No balances for this month."
				/>
				{this.renderVehicleBalanceTotals()}
			</>
		);
	};

	render() {
		const newLeases = this.getNewLeasesForMonth(
			this.state.selectedMonth,
			this.state.selectedYear,
		);
		const termLeases = this.getTerminatedLeasesForMonth(
			this.state.selectedMonth,
			this.state.selectedYear,
		);
		const totalPeriod = this.getCurrentPeriodTotal();

		return (
			<div>
				<h5>Monthly Entry</h5>
				{!this.props.stateCalculations && <p>Loading...</p>}
				<div className="MonthPicker">
					<select
						value={this.state.selectedMonth}
						onChange={(e) =>
							this.setState({ selectedMonth: parseInt(e.target.value, 10) })
						}
					>
						{moment.months().map((name, index) => (
							<option key={index} value={index}>
								{name}
							</option>
						))}
					</select>
					<select
						value={this.state.selectedYear}
						onChange={(e) =>
							this.setState({ selectedYear: parseInt(e.target.value, 10) })
						}
					>
						{[
							2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030,
						].map((year) => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
					</select>
					<CSVLink
						data={this.getMonthlyEntryCSV()}
						filename={`MonthlyEntry_${moment.months()[this.state.selectedMonth]}_${this.state.selectedYear}.csv`}
						title="Download CSV"
					>
						<IconBuilder extension="csv" size={32} />
					</CSVLink>
				</div>
				<div className="MonthlyTotals">
					<div>
						<h6>
							Totals for the month of{" "}
							{moment({
								year: this.state.selectedYear,
								month: this.state.selectedMonth,
							}).format("MMMM YYYY")}
						</h6>
						<Table striped bordered hover size="sm">
							<thead>
								<tr>
									<th>State</th>
									<th>(dr) ROU Asset Amort Exp</th>
									<th>(cr) Right of Use Asset</th>
									<th>(dr) Lease Liability</th>
									<th>(dr) Interest Exp</th>
									<th>(cr) Cash</th>
								</tr>
							</thead>
							<tbody>
								{this.props.stateCalculations &&
									Object.keys(this.props.stateCalculations).map((state) => {
										const period = this.getCurrentPeriodForState(state);
										return (
											<tr key={state}>
												<td>{state}</td>
												<td>
													{period
														? "$" +
															period.ROUAssetAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)
														: "-"}
												</td>
												<td>
													{period
														? "$" +
															period.ROUAssetAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)
														: "-"}
												</td>
												<td>
													{period
														? "$" +
															period.LeaseLiabilityAmortization?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)
														: "-"}
												</td>
												<td>
													{period
														? "$" +
															period.ImputedInterest?.toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																},
															)
														: "-"}
												</td>
												<td>
													{period
														? "$" +
															period.Payment?.toLocaleString(undefined, {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															})
														: "-"}
												</td>
											</tr>
										);
									})}
							</tbody>
							{/* Row totals taken from fullCalculations */}
							{totalPeriod && (
								<tr key="total" style={{ fontWeight: "bold" }}>
									<td>Total</td>
									<td>
										{"$" +
											totalPeriod.ROUAssetAmortization?.toLocaleString(
												undefined,
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}
									</td>
									<td>
										{"$" +
											totalPeriod.ROUAssetAmortization?.toLocaleString(
												undefined,
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}
									</td>
									<td>
										{"$" +
											totalPeriod.LeaseLiabilityAmortization?.toLocaleString(
												undefined,
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												},
											)}
									</td>
									<td>
										{"$" +
											totalPeriod.ImputedInterest?.toLocaleString(undefined, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
									</td>
									<td>
										{"$" +
											totalPeriod.Payment?.toLocaleString(undefined, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
									</td>
								</tr>
							)}
						</Table>
					</div>
				</div>
				<div className="MonthlyEntrySubTables">
					<div className="NewLeases">
						<h6>
							{newLeases ? "New Lease Entries for" : "No new leases for"}{" "}
							{moment({
								year: this.state.selectedYear,
								month: this.state.selectedMonth,
							}).format("MMMM YYYY")}
						</h6>
						{newLeases && (
							<Table striped bordered hover size="sm">
								<thead>
									<tr>
										<th>State</th>
										<th>(dr) Right of Use Asset</th>
										<th>(cr) Lease Liability</th>
									</tr>
								</thead>
								<tbody>
									{Object.keys(newLeases).map((state) => (
										<tr key={state}>
											<td>{state}</td>
											<td>
												{"$" +
													newLeases[state].NetROUAsset?.toLocaleString(
														undefined,
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}
											</td>
											<td>
												{"$" +
													newLeases[state].NetLeaseLiability?.toLocaleString(
														undefined,
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
					</div>
					<div className="TerminatedLeases">
						<h6>
							{termLeases
								? "Terminated Leases for"
								: "No terminated leases for"}{" "}
							{moment({
								year: this.state.selectedYear,
								month: this.state.selectedMonth,
							}).format("MMMM YYYY")}
						</h6>
						{termLeases && (
							<Table striped bordered hover size="sm">
								<thead>
									<tr>
										<th>State</th>
										<th>(dr) Lease Liability</th>
										<th>(cr) Right of Use Asset</th>
									</tr>
								</thead>
								<tbody>
									{Object.keys(termLeases).map((state) => (
										<tr key={state}>
											<td>{state}</td>
											<td>
												{"$" +
													termLeases[
														state
													].LeaseLiabilityAmortization?.toLocaleString(
														undefined,
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}
											</td>
											<td>
												{"$" +
													termLeases[
														state
													].ROUAssetAmortization?.toLocaleString(undefined, {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
					</div>
				</div>
				<div className="VehicleBalancesSection">
					<div
						className="vehicle-balances-header"
						onClick={this.toggleVehicleBalances}
					>
						<p>Balances by Vehicle</p>
						<span className="material-icons">
							{this.state.showVehicleBalances
								? "expand_less"
								: "expand_more"}
						</span>
					</div>
					{this.state.showVehicleBalances && this.renderVehicleBalances()}
				</div>
			</div>
		);
	}
}

export default MonthlyEntry;
